import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import type {
  TrustStripItem,
  TrustStripSettings,
  TrustStripStyle,
} from '../../../../services/ecommerceApi';

export const DEFAULT_TRUST_STRIP: TrustStripSettings = {
  items: [
    { value: '1000+', label: 'Aktif İşletme' },
    { value: '50K+', label: 'Aylık İşlem' },
    { value: '99.9%', label: 'Sistem Uptime' },
    { value: '7/24', label: 'Destek Hizmeti' },
  ],
  backgroundColor: 'rgba(30, 64, 175, 0.55)',
  textColor: '#ffffff',
  mutedColor: 'rgba(255,255,255,0.85)',
  style: 'columns',
};

const TRUST_STRIP_STYLES: TrustStripStyle[] = ['columns', 'inline', 'editorial'];

const TEMPLATE_PRESETS: Record<
  TrustStripStyle,
  Pick<TrustStripSettings, 'style' | 'backgroundColor' | 'textColor' | 'mutedColor'>
> = {
  columns: {
    style: 'columns',
    backgroundColor: 'rgba(30, 64, 175, 0.55)',
    textColor: '#ffffff',
    mutedColor: 'rgba(255,255,255,0.85)',
  },
  inline: {
    style: 'inline',
    backgroundColor: 'rgba(30, 64, 175, 0.55)',
    textColor: '#ffffff',
    mutedColor: 'rgba(255,255,255,0.85)',
  },
  editorial: {
    style: 'editorial',
    backgroundColor: '#f6f4f0',
    textColor: '#161616',
    mutedColor: '#5c5c5c',
  },
};

const normalizeStyle = (style?: string): TrustStripStyle =>
  style === 'inline' || style === 'editorial' ? style : 'columns';

const TrustStripLivePreview: React.FC<{ settings: TrustStripSettings }> = ({ settings }) => {
  const items = (settings.items || []).filter((i) => i.value || i.label);
  const cols = Math.min(items.length || 1, 4);
  const isEditorial = settings.style === 'editorial';
  const isInline = settings.style === 'inline';
  const textColor = settings.textColor || (isEditorial ? '#161616' : '#ffffff');
  const mutedColor =
    settings.mutedColor || (isEditorial ? '#5c5c5c' : textColorToMuted(textColor));
  const previewMaxWidth =
    isInline || cols >= 4 ? '100%' : cols === 3 ? 540 : cols === 2 ? 320 : 160;

  return (
    <Box
      sx={{
        mt: 1,
        px: 3,
        py: 2.5,
        borderRadius: isEditorial ? 0 : 3,
        bgcolor: settings.backgroundColor || (isEditorial ? '#f6f4f0' : 'rgba(30,64,175,0.55)'),
        display: isInline ? 'flex' : 'grid',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: isInline || cols < 4 ? 'center' : undefined,
        justifyItems: !isInline && cols < 4 ? 'center' : undefined,
        gridTemplateColumns: isInline
          ? undefined
          : cols >= 4
            ? 'repeat(4, 1fr)'
            : `repeat(${cols}, minmax(0, 1fr))`,
        width: previewMaxWidth,
        maxWidth: '100%',
        mx: 'auto',
        gap: isInline ? 1.5 : 2,
        textAlign: isEditorial ? 'left' : 'center',
        border: isEditorial ? '1px solid #e6e2dc' : '1px solid rgba(255,255,255,0.25)',
        borderTop: isEditorial ? '1px solid #e6e2dc' : undefined,
        backdropFilter: isEditorial ? 'none' : 'blur(8px)',
      }}
    >
      {items.map((item, index) => (
        <Box
          key={`preview-${index}`}
          sx={{
            display: isInline ? 'inline-flex' : 'block',
            alignItems: 'baseline',
            gap: 0.75,
          }}
        >
          {isInline && index > 0 && (
            <Typography component="span" sx={{ color: mutedColor, mr: 0.5 }}>
              ·
            </Typography>
          )}
          <Typography
            sx={{
              fontWeight: isEditorial ? 500 : 800,
              fontFamily: isEditorial ? 'Georgia, "Times New Roman", serif' : 'inherit',
              fontSize: isInline ? 14 : { xs: 18, md: 24 },
              color: textColor,
              display: isInline ? 'inline' : 'block',
            }}
          >
            {item.value}
          </Typography>
          <Typography
            sx={{
              fontSize: isInline ? 13 : 13,
              color: mutedColor,
              display: isInline ? 'inline' : 'block',
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const TrustStripTemplateThumb: React.FC<{ style: TrustStripStyle; selected: boolean }> = ({
  style,
}) => {
  if (style === 'editorial') {
    return (
      <Box
        sx={{
          bgcolor: '#f6f4f0',
          border: '1px solid #e6e2dc',
          borderRadius: 1,
          px: 1,
          py: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0.75,
        }}
      >
        {['31 yıl', '14 ofis'].map((value) => (
          <Box key={value}>
            <Typography
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontSize: 13,
                fontWeight: 500,
                color: '#161616',
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
            <Typography sx={{ fontSize: 9, color: '#5c5c5c' }}>örnek</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  if (style === 'inline') {
    return (
      <Box
        sx={{
          bgcolor: 'rgba(30,64,175,0.55)',
          borderRadius: 1.5,
          px: 1,
          py: 1.25,
          color: '#fff',
          fontSize: 10,
          textAlign: 'center',
        }}
      >
        1000+ · 50K+ · 7/24
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'rgba(30,64,175,0.55)',
        borderRadius: 1.5,
        px: 1,
        py: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 0.75,
        textAlign: 'center',
        color: '#fff',
      }}
    >
      {['1000+', '7/24'].map((value) => (
        <Box key={value}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
          <Typography sx={{ fontSize: 9, opacity: 0.8 }}>örnek</Typography>
        </Box>
      ))}
    </Box>
  );
};

/** Metin renginden etiket (muted) rengi üretir */
export function textColorToMuted(color: string, alpha = 0.82): string {
  const raw = (color || '').trim();
  if (!raw) return `rgba(255,255,255,${alpha})`;
  const hex = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgb = raw.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+\s*)?\)$/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
  return raw;
}

export const normalizeTrustStripSettings = (
  raw?: Partial<TrustStripSettings> | null
): TrustStripSettings => {
  const items = Array.isArray(raw?.items)
    ? raw!.items!
        .map((item) => ({
          value: String(item?.value || ''),
          label: String(item?.label || ''),
        }))
        .filter((item) => item.value || item.label)
    : [];

  return {
    items: items.length ? items.slice(0, 4) : [...(DEFAULT_TRUST_STRIP.items || [])],
    backgroundColor: raw?.backgroundColor || DEFAULT_TRUST_STRIP.backgroundColor,
    textColor: raw?.textColor || DEFAULT_TRUST_STRIP.textColor,
    mutedColor: raw?.mutedColor || DEFAULT_TRUST_STRIP.mutedColor,
    style: normalizeStyle(raw?.style),
  };
};

interface TrustStripSettingsPanelProps {
  enabled: boolean;
  settings: TrustStripSettings;
  onEnabledChange: (enabled: boolean) => void;
  onSettingsChange: (settings: TrustStripSettings) => void;
  onSave: () => void | Promise<void>;
  saving?: boolean;
  saved?: boolean;
  error?: string | null;
  compact?: boolean;
}

const TrustStripSettingsPanel: React.FC<TrustStripSettingsPanelProps> = ({
  enabled,
  settings,
  onEnabledChange,
  onSettingsChange,
  onSave,
  saving = false,
  saved = false,
  error = null,
  compact = false,
}) => {
  const { t } = useTranslation('salesSystem');
  const trustStrip = normalizeTrustStripSettings(settings);

  const patchItem = (index: number, partial: Partial<TrustStripItem>) => {
    const items = [...(trustStrip.items || [])];
    items[index] = { ...items[index], ...partial };
    onSettingsChange({ ...trustStrip, items });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight="bold">
          {t('ecommerceHomeSettings.trustStrip.title')}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              size="small"
            />
          }
          label={t('ecommerceHomeSettings.trustStrip.enabled')}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceHomeSettings.trustStrip.hint')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {enabled && (
        <Grid container spacing={2}>
          {(trustStrip.items || []).map((item, index) => (
            <Grid item xs={12} key={`trust-item-${index}`}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.value')}
                    value={item.value || ''}
                    onChange={(e) => patchItem(index, { value: e.target.value })}
                    placeholder="1000+"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('ecommerceHomeSettings.trustStrip.label')}
                    value={item.label || ''}
                    onChange={(e) => patchItem(index, { label: e.target.value })}
                    placeholder="Aktif İşletme"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={(trustStrip.items || []).length <= 1}
                    onClick={() => {
                      onSettingsChange({
                        ...trustStrip,
                        items: (trustStrip.items || []).filter((_, i) => i !== index),
                      });
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          ))}

          {(trustStrip.items || []).length < 4 && (
            <Grid item xs={12}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  onSettingsChange({
                    ...trustStrip,
                    items: [...(trustStrip.items || []), { value: '', label: '' }],
                  })
                }
              >
                {t('ecommerceHomeSettings.trustStrip.addItem')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {t('ecommerceHomeSettings.trustStrip.template')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1.5,
              }}
            >
              {TRUST_STRIP_STYLES.map((style) => {
                const selected = (trustStrip.style || 'columns') === style;
                const labelKey =
                  style === 'editorial'
                    ? 'styleEditorial'
                    : style === 'inline'
                      ? 'styleInline'
                      : 'styleColumns';
                const hintKey = `${labelKey}Hint` as const;
                return (
                  <Box
                    key={style}
                    component="button"
                    type="button"
                    onClick={() =>
                      onSettingsChange({
                        ...trustStrip,
                        ...TEMPLATE_PRESETS[style],
                      })
                    }
                    sx={{
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1.25,
                      bgcolor: 'background.paper',
                      boxShadow: selected ? 1 : 0,
                    }}
                  >
                    <TrustStripTemplateThumb style={style} selected={selected} />
                    <Typography variant="subtitle2" sx={{ mt: 1, lineHeight: 1.3 }}>
                      {t(`ecommerceHomeSettings.trustStrip.${labelKey}`)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t(`ecommerceHomeSettings.trustStrip.${hintKey}`)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.backgroundColor')}
              value={trustStrip.backgroundColor || ''}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
              }
              helperText="Örn. rgba(30,64,175,0.55)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.backgroundColor || '').startsWith('#')
                          ? trustStrip.backgroundColor
                          : '#1e40af'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, backgroundColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.textColor')}
              value={trustStrip.textColor || '#ffffff'}
              onChange={(e) => {
                const textColor = e.target.value;
                onSettingsChange({
                  ...trustStrip,
                  textColor,
                  // Etiket rengi metin rengiyle birlikte güncellenir
                  mutedColor: textColorToMuted(textColor),
                });
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.textColor || '').startsWith('#')
                          ? trustStrip.textColor
                          : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const textColor = e.target.value;
                        onSettingsChange({
                          ...trustStrip,
                          textColor,
                          mutedColor: textColorToMuted(textColor),
                        });
                      }}
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHomeSettings.trustStrip.labelColor')}
              value={trustStrip.mutedColor || textColorToMuted(trustStrip.textColor || '#ffffff')}
              onChange={(e) =>
                onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
              }
              helperText={t('ecommerceHomeSettings.trustStrip.labelColorHint')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={
                        String(trustStrip.mutedColor || '').startsWith('#')
                          ? trustStrip.mutedColor
                          : String(trustStrip.textColor || '').startsWith('#')
                            ? trustStrip.textColor
                            : '#ffffff'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onSettingsChange({ ...trustStrip, mutedColor: e.target.value })
                      }
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TrustStripLivePreview settings={trustStrip} />
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
        <Button variant="contained" onClick={() => void onSave()} disabled={saving}>
          {saving
            ? t('ecommerceHomeSettings.trustStrip.saving')
            : enabled
              ? t('ecommerceHomeSettings.trustStrip.save')
              : t('ecommerceHomeSettings.trustStrip.saveHidden')}
        </Button>
        {saved && (
          <Typography variant="body2" color="success.main">
            {t('ecommerceHomeSettings.trustStrip.saved')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TrustStripSettingsPanel;
